/**
 * 全量导出日志
 */
import dayjs from 'dayjs';
import i18n, { i18n_t } from '@/locales/i18n';
import store from '@/renderer/store';
import tips from '@/renderer/scripts/utils/tips';
import { IS_BROWSER } from '@/common/constant/index';
import { exportCSVInBrowser, exportCSVInPC } from '@/renderer/scripts/utils/tools/index';
import { getDownloadPath } from '@/renderer/scripts/utils/download/downloadHelper';
import smh, { SMH } from '../smh';

let fs: typeof import('fs');
let path: typeof import('path');
if (!IS_BROWSER) {
  fs = (window.parent as any).require('fs') as typeof import('fs');
  path = (window.parent as any).require('path') as typeof import('path');
}
type Status = 'waiting' | 'running' | 'finished' | 'failed' | 'paused' | 'canceled';
interface LoopData {
  status: Status;
  list: any[];
}

export class LogExporter {
  // 日志查询条件
  private conditions: SMH.CreateSearchLogTaskParams | {} = {};
  private searchId = '';
  private columns: any[] = [];
  private status: Status = 'waiting';

  // 单条日志文件容纳最多记录，对于大量日志需要分文件下载
  private LIMIT = 5000;

  // 缓存查询的列表，导出时用
  private logList: any[] = [];
  private slice = 0;

  // 下载到本地目录
  private downloadTarget = '';

  // 对外的导出方法
  public export = async (columns: any[], conditions: SMH.CreateSearchLogTaskParams): Promise<void> => {
    this.columns = columns;
    this.conditions = conditions;
    this.slice = 0;

    // pc端获得下载路径
    if (!IS_BROWSER) {
      this.downloadTarget = await getDownloadPath();
      if (!this.downloadTarget) return;
    }
    try {
      this.status = 'running';
      store.commit('setLogExporting', true);
      const res = await this.startLoop({}, []);
      this.status = res.status;
      this.listUpdate([]);
      if (this.status === 'finished') {
        this.slice = 0;
        tips.success(i18n_t('日志导出完成'));
      }
      store.commit('setLogExporting', false);
    } catch (e) {
      tips.error(e as MsgError);
      store.commit('setLogExporting', false);
      this.cancelSearchTask();
    }
  };
  private startLoop = async (params: any, list: any[]): Promise<LoopData> => {
    if (this.status === 'canceled') {
      return {
        status: 'canceled',
        list: [],
      };
    }
    try {
      const newParams = {
        ...this.conditions,
        ...params,
      };
      const res = await smh.userAPI.createSearchLogTask(newParams);
      const { data } = res;
      const newList = [...list, ...data.contents];
      this.listUpdate(data.contents);
      this.searchId = data.searchId;
      if (data.hasMore) {
        // eslint-disable-next-line no-return-await
        return await this.loopSearch(data.nextMarker, newList);
      }
      return {
        status: 'finished',
        list: newList,
      };
    } catch (e) {
      tips.error(e as MsgError);
      return {
        status: 'failed',
        list: [],
      };
    }
  };
  private loopSearch = async (marker: number, list: any[]): Promise<LoopData> => {
    if (this.status === 'canceled') {
      return {
        status: 'canceled',
        list: [],
      };
    }
    let resList = [];
    try {
      const res = await smh.userAPI.listSearchLog({
        searchId: this.searchId,
        marker,
      });
      const { data } = res;
      const newList = [...list, ...data.contents];
      resList = newList;
      this.listUpdate(data.contents);
      if (!data.hasMore) {
        // 搜索结束
        return {
          status: 'finished',
          list: newList,
        };
      }
      if (data.hasMore && data.contents && data.contents.length > 0) {
        // 继续搜索
        return await this.loopSearch(data.nextMarker, newList);
      }
      // es搜索达到上限，需要重新开启一个搜索任务继续搜索
      return this.startLoop(
        {
          searchAfterId: data.lastHit.id,
          endTime: data.lastHit.operationTime,
        },
        newList,
      );
    } catch (e) {
      tips.error(e as MsgError);
      store.commit('setLogExporting', false);
      this.cancelSearchTask();
      return {
        status: 'failed',
        list: [],
      };
    }
  };

  // 监听日志列表,达到limit后执行导出
  private listUpdate = (list: any[]) => {
    if (this.status === 'canceled') {
      return;
    }
    this.logList = [...this.logList, ...list];
    if (this.logList.length > this.LIMIT) {
      ++this.slice;
      const exportList = this.logList.slice(0, this.LIMIT);
      this.exportToDownload(exportList, this.slice);
      this.logList = this.logList.splice(this.LIMIT);
    } else if (this.status === 'finished') {
      // 处理未达到上限的列表或达到上限的列表最后一段
      if (this.slice === 0) {
        this.exportToDownload(this.logList);
      } else {
        ++this.slice;
        const exportList = this.logList.slice(0, this.LIMIT);
        this.exportToDownload(exportList, this.slice);
        this.logList = this.logList.splice(this.LIMIT);
      }
    }
  };

  // 导出到本地
  private exportToDownload = async (list: any[], slice?: number) => {
    const timeRangeStr =
      `${dayjs((this.conditions as SMH.CreateSearchLogTaskParams).startTime).format('YYYY-MM-DD')}_` +
      `${dayjs((this.conditions as SMH.CreateSearchLogTaskParams).endTime).format('YYYY-MM-DD')}`;
    const exportName = i18n_t('导出日志_{timeRangeStr}{slot1}.csv', {
      timeRangeStr,
      slot1: slice ? `_part${slice}` : '',
    });
    if (IS_BROWSER) {
      exportCSVInBrowser(this.columns, list, exportName);
    } else {
      exportCSVInPC(this.columns, list, exportName, this.downloadTarget);
    }
  };

  // 取消搜索任务
  private cancelSearchTask = async () => {
    if (this.searchId) {
      try {
        await smh.userAPI.delSearchInLog({
          searchId: this.searchId,
        });
        this.searchId = '';
      } catch (e) {
        /* i18n-ignore*/
        logger.ui.error('取消搜索日志失败');
      }
    }
  };

  // 暂不支持 暂停
  public pause = (): void => {
    logger.ui('paused');
  };

  // 暂不支持 暂停后继续
  public continue = (): void => {
    logger.ui('continue');
  };

  // 取消导出
  public cancel = (): void => {
    if (this.status === 'running') {
      this.status = 'canceled';
      this.cancelSearchTask();
    }
  };
}
export default new LogExporter();
